import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Checkbox, Tag, Popconfirm, message } from 'antd';
import { PlusOutlined, SafetyOutlined, EditOutlined } from '@ant-design/icons';
import {
  AdminRole,
  MenuDef,
  getRoleList,
  createRole,
  updateRole,
  updateRoleStatus,
  getMenuList,
} from '../services/adminAccountApi';

const COLORS = {
  primary: '#1677ff',
  secondary: '#722ed1',
};

const PageHeader: React.FC<{ title: string }> = ({ title }) => (
  <div style={{ marginBottom: 24 }}>
    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
      <SafetyOutlined style={{ marginRight: 8, color: COLORS.primary }} />
      {title}
    </h2>
    <div style={{ width: 40, height: 3, background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`, borderRadius: 2, marginTop: 8 }} />
  </div>
);

const RoleManagement: React.FC = () => {
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [menus, setMenus] = useState<MenuDef[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState<AdminRole | null>(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [roleData, menuData] = await Promise.all([getRoleList(), getMenuList()]);
      setRoles(roleData);
      setMenus(menuData);
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (values: { name: string; description?: string; menuKeys: string[] }) => {
    try {
      await createRole({
        name: values.name,
        description: values.description,
        menuKeys: values.menuKeys.join(','),
      });
      message.success('创建成功');
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchData();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleEdit = async (values: { name: string; description?: string; menuKeys: string[] }) => {
    if (!editingRole) return;
    try {
      await updateRole({
        id: editingRole.id,
        name: values.name,
        description: values.description,
        menuKeys: values.menuKeys.join(','),
      });
      message.success('更新成功');
      setEditModalVisible(false);
      editForm.resetFields();
      setEditingRole(null);
      fetchData();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: number) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      await updateRoleStatus(id, newStatus);
      message.success(newStatus === 1 ? '已启用' : '已禁用');
      fetchData();
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const openEdit = (role: AdminRole) => {
    setEditingRole(role);
    editForm.setFieldsValue({
      name: role.name,
      description: role.description,
      menuKeys: role.menuKeys ? role.menuKeys.split(',') : [],
    });
    setEditModalVisible(true);
  };

  const menuOptions = menus.map(m => ({ label: m.label, value: m.key }));

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '角色名', dataIndex: 'name', key: 'name', width: 150 },
    { title: '描述', dataIndex: 'description', key: 'description', width: 200 },
    {
      title: '菜单权限', dataIndex: 'menuKeys', key: 'menuKeys',
      render: (menuKeys: string) => {
        if (!menuKeys) return '-';
        const keys = menuKeys.split(',');
        const menuMap = new Map(menus.map(m => [m.key, m.label]));
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {keys.map(k => (
              <Tag key={k} color="blue" style={{ margin: 0 }}>{menuMap.get(k) || k}</Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 100,
      render: (status: number, record: AdminRole) => (
        <Popconfirm
          title={status === 1 ? '确认禁用该角色？' : '确认启用该角色？'}
          onConfirm={() => handleToggleStatus(record.id, status)}
        >
          <Tag color={status === 1 ? 'success' : 'error'} style={{ cursor: 'pointer' }}>
            {status === 1 ? '启用' : '禁用'}
          </Tag>
        </Popconfirm>
      ),
    },
    { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 170 },
    {
      title: '操作', key: 'action', width: 80,
      render: (_: any, record: AdminRole) => (
        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
          编辑
        </Button>
      ),
    },
  ];

  const renderMenuForm = () => (
    <Form.Item name="menuKeys" label="菜单权限" rules={[{ required: true, message: '请选择至少一个菜单' }]}>
      <Checkbox.Group options={menuOptions} style={{ display: 'flex', flexDirection: 'column', gap: 8 }} />
    </Form.Item>
  );

  return (
    <div>
      <PageHeader title="角色管理" />
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
          添加角色
        </Button>
      </div>
      <Table
        dataSource={roles}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="middle"
        pagination={false}
      />

      {/* 创建角色 Modal */}
      <Modal
        title="添加角色"
        open={createModalVisible}
        onCancel={() => { setCreateModalVisible(false); createForm.resetFields(); }}
        onOk={() => createForm.submit()}
        okText="创建"
        width={520}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="name" label="角色名称" rules={[{ required: true, message: '请输入角色名称' }]}>
            <Input placeholder="请输入角色名称" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea placeholder="请输入角色描述" rows={2} />
          </Form.Item>
          {renderMenuForm()}
        </Form>
      </Modal>

      {/* 编辑角色 Modal */}
      <Modal
        title="编辑角色"
        open={editModalVisible}
        onCancel={() => { setEditModalVisible(false); editForm.resetFields(); setEditingRole(null); }}
        onOk={() => editForm.submit()}
        okText="保存"
        width={520}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item name="name" label="角色名称" rules={[{ required: true, message: '请输入角色名称' }]}>
            <Input placeholder="请输入角色名称" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea placeholder="请输入角色描述" rows={2} />
          </Form.Item>
          {renderMenuForm()}
        </Form>
      </Modal>
    </div>
  );
};

export default RoleManagement;
